import { DestroyRef, Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PartnerIncomeTaxDocumentDto } from '@interfaces/partner-income-tax/responses/partner-income-tax.response';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnerIncomeTaxService } from '@services/http/partner-income-tax/partner-income-tax.service';
import { Subscription, finalize, first } from 'rxjs';
import {
  PartnerIncomeTaxStateService,
  describeApiError,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/partner-income-tax-state.service';
import {
  describeDocumentsSummary,
  describeReadResult,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/utils/partner-income-tax-format.util';

/** Limite do backend por pedido de leitura. */
export const MAX_DOCUMENTS_PER_READ = 50;

/**
 * Lista "Declarações no Data Room" (adendo v3 do card 3515) e a leitura seletiva. Mora fora do componente porque três
 * lugares a usam: a linha de contexto do topo (resumo e "Ver"), o título da aba interna Documentos (contagem e "n a
 * ler") e o próprio bloco, que só existe quando a aba Documentos está aberta.
 *
 * Provido pelo componente da aba (`PartnerIncomeTaxComponent`). Relê sempre que a visão geral é relida: o evento de
 * tempo real `partner-income-tax.changed` já faz o state service reler a visão geral, então a lista acompanha sem
 * polling.
 */
@Injectable()
export class DataRoomDocumentsStore {
  private readonly http = inject(PartnerIncomeTaxService);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly state = inject(PartnerIncomeTaxStateService);

  readonly documents = signal<PartnerIncomeTaxDocumentDto[]>([]);
  readonly loading = signal(false);
  readonly loaded = signal(false);
  readonly error = signal<string | null>(null);
  readonly reading = signal(false);

  /** Não lidos e com erro: alimentam o "n a ler" da aba e o atalho de seleção. */
  readonly pending = computed(() =>
    this.documents().filter(doc => doc.status === 'notRead' || doc.status === 'failed')
  );
  readonly total = computed(() => this.documents().length);
  /** "5 documentos lidos" ou "5 documentos · 3 lidos · 2 não lidos". */
  readonly summary = computed(() => describeDocumentsSummary(this.documents()));

  private loadSub: Subscription | null = null;
  private lastCompanyId: string | null = null;

  constructor() {
    // A visão geral é relida na troca de empresa, no "Tentar de novo" e no evento de tempo real: a lista vai junto.
    effect(() => {
      const overview = this.state.overview();
      untracked(() => {
        if (!overview) return;
        if (overview.companyId !== this.lastCompanyId) {
          this.lastCompanyId = overview.companyId;
          this.documents.set([]);
          this.loaded.set(false);
        }
        this.load(this.loaded());
      });
    });
    this.destroyRef.onDestroy(() => this.loadSub?.unsubscribe());
  }

  retry(): void {
    this.load(false);
  }

  /**
   * Manda ler os documentos escolhidos. Toast com os enviados e, à parte, os recusados com o motivo do backend; as
   * linhas enviadas viram "Na fila" na hora (N20), sem esperar a releitura. `done` roda no sucesso (o bloco limpa a
   * seleção).
   */
  read(chosen: PartnerIncomeTaxDocumentDto[], done: () => void): void {
    const companyId = this.state.companyId();
    if (!companyId || !chosen.length || this.reading()) return;
    if (chosen.length > MAX_DOCUMENTS_PER_READ) {
      this.toast.showToastWarn(
        `Escolha até ${MAX_DOCUMENTS_PER_READ} documentos por vez`,
        `Há ${chosen.length} marcados. Desmarque alguns e use Extrair dados de novo.`
      );
      return;
    }
    const names = new Map(chosen.map(doc => [doc.documentId, doc.fileName]));
    this.reading.set(true);
    this.http
      .readDocuments(companyId, { documentIds: chosen.map(doc => doc.documentId) })
      .pipe(
        first(),
        finalize(() => this.reading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: response => {
          const result = describeReadResult(response ?? { queued: 0, skipped: [] }, names);
          if (result.success) this.toast.showToastSuccess(result.success);
          if (result.skipped) {
            this.toast.showToastWarn(
              result.success ? 'Alguns documentos não foram enviados' : 'Nenhum documento enviado',
              result.skipped
            );
          }
          if (!result.success && !result.skipped) {
            this.toast.showToastWarn('Nenhum documento enviado para leitura');
          }
          const skipped = new Set((response?.skipped ?? []).map(item => item.documentId));
          const sent = new Set(chosen.map(doc => doc.documentId).filter(id => !skipped.has(id)));
          if (sent.size) {
            this.documents.update(list =>
              list.map(doc =>
                sent.has(doc.documentId)
                  ? { ...doc, status: 'queued' as const, message: null }
                  : doc
              )
            );
          }
          done();
          // O backend publica o evento de tempo real; reler confirma o status sem esperar por ele.
          this.state.reload(true);
        },
        error: error =>
          this.toast.showToastError(
            describeApiError(
              error,
              'Não conseguimos mandar os documentos para leitura. Nada foi enviado; use Extrair dados de novo.'
            )
          ),
      });
  }

  /** Reenvia a lista como cópia (o `app-table` é OnPush e precisa de uma referência nova para redesenhar caixas). */
  refreshReference(): void {
    this.documents.set([...this.documents()]);
  }

  /** `silent`: releitura (tempo real) sem esqueleto nem piscar o bloco. */
  private load(silent: boolean): void {
    const companyId = this.state.companyId();
    if (!companyId) return;
    this.loadSub?.unsubscribe();
    if (!silent) this.loading.set(true);
    this.error.set(null);
    this.loadSub = this.http
      .getDocuments(companyId)
      .pipe(
        first(),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: documents => {
          if (companyId !== this.state.companyId()) return;
          this.documents.set(documents ?? []);
          this.loaded.set(true);
        },
        error: error => {
          if (silent && this.loaded()) return;
          this.error.set(
            describeApiError(
              error,
              'Não conseguimos listar as declarações do Data Room agora. Use Tentar de novo.'
            )
          );
        },
      });
  }
}
